import type { FileResponse } from 'types/documentStorage';
import type { CreateOnlyOfficeSessionArgs, DocumentAction, DocumentContext, DocumentMode, OnlyOfficeSession } from 'types/documentEditing';
import { GUID_REGEX } from 'utils/regex';
import { splitFileNameAndExtension } from 'utils/file/fileUtils';
import { isRecord } from 'utils/typeChecks';
import { createDocumentSessionSchema } from 'utils/validations/documentSessionSchema';

export const getDocumentEditingOrigin = (): string | null => {
  if (process.env.ONLYOFFICE_ENABLED !== 'true') return null;
  try {
    const url = new URL(process.env.ONLYOFFICE_ORIGIN ?? '');
    if (url.protocol !== 'https:' || url.username || url.password || url.pathname !== '/' || url.search || url.hash) return null;
    return url.origin;
  } catch {
    return null;
  }
};

export const getDocumentExtension = (file: Pick<FileResponse, 'fileType' | 'name'>): string => {
  const extension = (file.fileType?.trim() || splitFileNameAndExtension(file.name).extension).replace(/^\./, '').toLowerCase();
  if (['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/docx'].includes(extension)) return 'docx';
  if (['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/xlsx'].includes(extension)) return 'xlsx';
  if (['application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/pptx'].includes(extension)) return 'pptx';
  return extension;
};

export const getDocumentActions = (file: FileResponse, context?: DocumentContext): DocumentAction[] => {
  if (!context || context.isHistorical || !getDocumentEditingOrigin()) return [];
  const extension = getDocumentExtension(file);
  if (!['docx', 'xlsx', 'pptx'].includes(extension) || !['GoogleCloudStorage', 'DMS'].includes(file.typeOfStorage)) return [];
  const { agendaId, recordId, persistedFileIds, canEdit } = context;
  const disabled = !GUID_REGEX.test(agendaId) || !GUID_REGEX.test(recordId) || !GUID_REGEX.test(file.id) || !persistedFileIds.includes(file.id);
  const modes: DocumentMode[] = ['view'];
  if (canEdit && file.typeOfStorage === 'GoogleCloudStorage' && file.isReadOnlySnapshot === false && !file.archiveFileInfo) {
    modes.push('edit');
    if (extension === 'docx') modes.push('review');
  }
  if (
    context.canComment &&
    ['docx', 'xlsx'].includes(extension) &&
    file.typeOfStorage === 'GoogleCloudStorage' &&
    file.isReadOnlySnapshot === false &&
    !file.archiveFileInfo
  ) {
    modes.push('comment');
  }
  return modes.map((mode) => ({ mode, disabled }));
};

export const getDocumentReturnPath = (value: string | null, origin: string): string => {
  try {
    const url = new URL(value ?? '/user', origin);
    if (url.origin !== origin || (url.pathname !== '/user' && !url.pathname.startsWith('/user/')) || url.pathname.startsWith('/user/documents/')) {
      return '/user';
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '/user';
  }
};

export const getDocumentRouteArgs = (
  params: { agendaId?: string; recordId?: string; fileId?: string },
  search: string
): CreateOnlyOfficeSessionArgs | null => {
  const { agendaId = '', recordId = '', fileId = '' } = params;
  const mode = new URLSearchParams(search).get('mode') ?? 'view';
  if (!GUID_REGEX.test(agendaId) || !GUID_REGEX.test(recordId) || !GUID_REGEX.test(fileId) || !isDocumentMode(mode)) return null;
  return { agendaId, recordId, fileId, mode, suppressErrorToast: true };
};

export const isDocumentMode = (value: unknown): value is DocumentMode =>
  value === 'view' || value === 'edit' || value === 'review' || value === 'comment';

export const validateDocumentSession = (value: unknown, fileId: string, origin: string, now = Date.now()): OnlyOfficeSession => {
  const schema = createDocumentSessionSchema(fileId, origin, now);
  const session = schema.validateSync(value, { strict: true });
  return schema.cast(session, { stripUnknown: true });
};

export const getDocumentErrorKey = (error: unknown): string => {
  const status = isRecord(error) ? error.status : undefined;
  switch (status) {
    case 400:
      return 'user.documentEditor.invalidDocument';
    case 401:
      return 'user.documentEditor.authenticationRequired';
    case 403:
      return 'user.documentEditor.forbidden';
    case 404:
      return 'user.documentEditor.notFound';
    case 409:
      return 'user.documentEditor.conflict';
    default:
      return 'user.documentEditor.unavailable';
  }
};
